import { z } from 'zod';
import {
    PillarPaymentRequestType,
    type PillarProcessCardTokenPaymentRequest,
    type PillarTerminalSaleRequest,
} from '../../../payment.types';
import {
    CloverSidebarPaymentEntryActionType,
    CloverSidebarPaymentEntryOutcome,
    type CloverSidebarPaymentEntryAction,
    type CloverSidebarPaymentEntryNormalizedResult,
    type CloverSidebarPaymentEntryRequest,
    type CloverSidebarPaymentEntryRequestDetails,
    type CloverSidebarPaymentEntryResponseDetails,
    type CloverSupportedSidebarPaymentEntryAction,
    type CloverUnsupportedSidebarPaymentEntryAction,
} from '../../../types/ipg/clover/clover-sidebar-payment-entry.types';
import {
    ProcessCardTokenPaymentRequestSchema,
    TerminalSaleRequestSchema,
    TransactionDetailsSchema,
} from '../../payment.schemas';

export const CloverTerminalSaleSidebarPaymentEntryActionSchema: z.ZodObject<{
    actionType: z.ZodLiteral<
        typeof CloverSidebarPaymentEntryActionType.TERMINAL_SALE
    >;
    request: typeof TerminalSaleRequestSchema;
}> = z
    .object({
        actionType: z.literal(CloverSidebarPaymentEntryActionType.TERMINAL_SALE),
        request: TerminalSaleRequestSchema,
    })
    .strict();

export const CloverTokenSaleSidebarPaymentEntryActionSchema: z.ZodObject<{
    actionType: z.ZodLiteral<
        typeof CloverSidebarPaymentEntryActionType.TOKEN_SALE
    >;
    request: typeof ProcessCardTokenPaymentRequestSchema;
}> = z
    .object({
        actionType: z.literal(CloverSidebarPaymentEntryActionType.TOKEN_SALE),
        request: ProcessCardTokenPaymentRequestSchema,
    })
    .strict();

export const CloverStoredTokenSaleSidebarPaymentEntryActionSchema: z.ZodObject<{
    actionType: z.ZodLiteral<
        typeof CloverSidebarPaymentEntryActionType.STORED_TOKEN_SALE
    >;
    request: typeof ProcessCardTokenPaymentRequestSchema;
}> = z
    .object({
        actionType: z.literal(
            CloverSidebarPaymentEntryActionType.STORED_TOKEN_SALE
        ),
        request: ProcessCardTokenPaymentRequestSchema,
    })
    .strict();

export const CloverUnsupportedSidebarPaymentEntryActionSchema: z.ZodObject<{
    actionType: z.ZodLiteral<
        typeof CloverSidebarPaymentEntryActionType.ACH_PAYMENT
    >;
}> = z
    .object({
        actionType: z.literal(CloverSidebarPaymentEntryActionType.ACH_PAYMENT),
    })
    .strict() satisfies z.ZodType<CloverUnsupportedSidebarPaymentEntryAction>;

export const CloverSupportedSidebarPaymentEntryActionSchema: z.ZodType<CloverSupportedSidebarPaymentEntryAction> =
    z.discriminatedUnion('actionType', [
        CloverTerminalSaleSidebarPaymentEntryActionSchema,
        CloverTokenSaleSidebarPaymentEntryActionSchema,
        CloverStoredTokenSaleSidebarPaymentEntryActionSchema,
    ]);

export const CloverSidebarPaymentEntryActionSchema: z.ZodType<CloverSidebarPaymentEntryAction> =
    z.discriminatedUnion('actionType', [
        CloverTerminalSaleSidebarPaymentEntryActionSchema,
        CloverTokenSaleSidebarPaymentEntryActionSchema,
        CloverStoredTokenSaleSidebarPaymentEntryActionSchema,
        CloverUnsupportedSidebarPaymentEntryActionSchema,
    ]);

export const CloverSidebarPaymentEntryRequestDetailsSchema: z.ZodObject<{
    action: typeof CloverSidebarPaymentEntryActionSchema;
}> = z
    .object({
        action: CloverSidebarPaymentEntryActionSchema,
    })
    .strict() satisfies z.ZodType<CloverSidebarPaymentEntryRequestDetails>;

export const CloverSidebarPaymentEntryTerminalSaleRequestSchema: z.ZodObject<{
    request_type: z.ZodLiteral<typeof PillarPaymentRequestType.TERMINAL_SALE>;
    request: typeof TerminalSaleRequestSchema;
    transaction_details: typeof TransactionDetailsSchema;
}> = z
    .object({
        request_type: z.literal(PillarPaymentRequestType.TERMINAL_SALE),
        request: TerminalSaleRequestSchema,
        transaction_details: TransactionDetailsSchema,
    })
    .strict() satisfies z.ZodType<PillarTerminalSaleRequest>;

export const CloverSidebarPaymentEntryTokenSaleRequestSchema: z.ZodObject<{
    request_type: z.ZodLiteral<
        typeof PillarPaymentRequestType.PROCESS_CARD_TOKEN_PAYMENT
    >;
    request: typeof ProcessCardTokenPaymentRequestSchema;
    transaction_details: typeof TransactionDetailsSchema;
}> = z
    .object({
        request_type: z.literal(
            PillarPaymentRequestType.PROCESS_CARD_TOKEN_PAYMENT
        ),
        request: ProcessCardTokenPaymentRequestSchema,
        transaction_details: TransactionDetailsSchema,
    })
    .strict() satisfies z.ZodType<PillarProcessCardTokenPaymentRequest>;

export const CloverSidebarPaymentEntryRequestSchema: z.ZodType<CloverSidebarPaymentEntryRequest> =
    z.discriminatedUnion('request_type', [
        CloverSidebarPaymentEntryTerminalSaleRequestSchema,
        CloverSidebarPaymentEntryTokenSaleRequestSchema,
    ]);

export const CloverSidebarPaymentEntryNormalizedResultSchema: z.ZodType<CloverSidebarPaymentEntryNormalizedResult> =
    z.object({
        actionType: z.enum([
            CloverSidebarPaymentEntryActionType.TERMINAL_SALE,
            CloverSidebarPaymentEntryActionType.TOKEN_SALE,
            CloverSidebarPaymentEntryActionType.STORED_TOKEN_SALE,
        ]),
        requestType: z.enum([
            PillarPaymentRequestType.TERMINAL_SALE,
            PillarPaymentRequestType.PROCESS_CARD_TOKEN_PAYMENT,
        ]),
        transactionDetails: TransactionDetailsSchema,
    });

export const CloverSidebarPaymentEntryBuiltResponseDetailsSchema: z.ZodObject<{
    outcome: z.ZodLiteral<typeof CloverSidebarPaymentEntryOutcome.BUILT>;
    paymentRequest: typeof CloverSidebarPaymentEntryRequestSchema;
    normalizedResult: typeof CloverSidebarPaymentEntryNormalizedResultSchema;
}> = z.object({
    outcome: z.literal(CloverSidebarPaymentEntryOutcome.BUILT),
    paymentRequest: CloverSidebarPaymentEntryRequestSchema,
    normalizedResult: CloverSidebarPaymentEntryNormalizedResultSchema,
});

export const CloverSidebarPaymentEntryUnsupportedActionResponseDetailsSchema: z.ZodObject<{
    outcome: z.ZodLiteral<
        typeof CloverSidebarPaymentEntryOutcome.UNSUPPORTED_ACTION
    >;
    actionType: z.ZodLiteral<
        typeof CloverSidebarPaymentEntryActionType.ACH_PAYMENT
    >;
    message: z.ZodString;
}> = z.object({
    outcome: z.literal(CloverSidebarPaymentEntryOutcome.UNSUPPORTED_ACTION),
    actionType: z.literal(CloverSidebarPaymentEntryActionType.ACH_PAYMENT),
    message: z.string().min(1),
});

export const CloverSidebarPaymentEntryResponseDetailsSchema: z.ZodType<CloverSidebarPaymentEntryResponseDetails> =
    z.discriminatedUnion('outcome', [
        CloverSidebarPaymentEntryBuiltResponseDetailsSchema,
        CloverSidebarPaymentEntryUnsupportedActionResponseDetailsSchema,
    ]);
